import React from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { DataTable as CommonDataTable } from "../../common/DataTable";

export const StatCard = ({ label, value, danger = false }) => (
  <div className={`border rounded-xl p-5 space-y-1 ${danger ? "border-red-200 bg-red-50" : "border-gray-200 bg-white"}`}>
    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</p>
    <p className={`text-2xl font-bold ${danger ? "text-red-700" : "text-gray-900"}`}>{value ?? "—"}</p>
  </div>
);

export const MetaCell = ({ label, value }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
    <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">{label}</p>
    <p className="text-sm font-medium text-gray-900">{value || "—"}</p>
  </div>
);

export const Expandable = ({ title, isOpen, onToggle, badge, children }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-5 py-3.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-sm font-semibold text-gray-800">{title}</span>
        {badge}
      </div>
      <ChevronDownIcon
        className={`w-4 h-4 flex-shrink-0 ml-3 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        style={{ color: "var(--app-primary-color, #26256b)" }}
      />
    </button>
    {isOpen && (
      <div className="px-5 py-5 border-t border-gray-100 bg-white space-y-5">
        {children}
      </div>
    )}
  </div>
);

export const SubExpandable = ({ title, isOpen, onToggle, children }) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left text-sm"
    >
      <span className="font-medium text-gray-700">{title}</span>
      <ChevronDownIcon
        className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        style={{ color: "var(--app-primary-color, #26256b)" }}
      />
    </button>
    {isOpen && (
      <div className="p-4 border-t border-gray-100 bg-white">
        {children}
      </div>
    )}
  </div>
);

export const DataTable = ({ headers, rows, title }) => {
  const columns = headers.map((h, colIndex) => ({
    id: `col-${colIndex}`,
    header: h,
    enableSorting: false,
    cell: ({ row }) => <span>{row.original[colIndex]}</span>,
  }));

  return (
    <div className="space-y-2">
      {title && <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</p>}
      <CommonDataTable
        columns={columns}
        data={rows}
        getRowId={(r, index) => String(index)}
        emptyMessage="No data available"
        enableColumnVisibility={false}
      />
    </div>
  );
};

export const SectionTitle = ({ title, count }) => (
  <div className="flex items-center gap-2 mb-4">
    <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
    {count !== undefined && (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">{count}</span>
    )}
  </div>
);

export const LastSynced = ({ value, formatFn }) => (
  <p className="text-xs text-gray-400 mt-1">
    Last synced: <span className="font-medium" style={{ color: "var(--app-primary-color, #26256b)" }}>{formatFn ? formatFn(value) : value ?? "Recently"}</span>
  </p>
);

export const ConnectedBadge = ({ connected = true }) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${connected ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
    {connected ? "Connected" : "Disconnected"}
  </span>
);

export const PermissionBadge = ({ ok }) => (
  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${ok ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
    {ok ? "Enabled" : "No Access"}
  </span>
);

export const EmptyState = ({ message }) => (
  <div className="py-10 text-center text-sm text-gray-400">{message}</div>
);
